import { useQuery } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge } from '../atoms';
import { StatCard } from './StatCard';

/**
 * CalcBreakdownPanel — renders the REAL tax-calc resolution trace for a single
 * tax record.
 *
 * Wires the frozen Domain-11 contract (src/routes/calc.js):
 *   GET /api/tax-records/:id/calc-breakdown
 *     → { tax_record_id, state, is_millage, steps: [{ term, label, input, output, rule }] }
 *
 * The endpoint is book-scoped + org-scoped server-side (an off-book id reads as
 * 404) and money-redacted at the response boundary: a money-blind role receives
 * `output`/`input: null` on the governed steps (tax_amount / taxable_value /
 * rate and anything derived from them). This panel renders those as "—"
 * transparently — it never softens or reconstructs the redaction, and it adds
 * no client-side money logic. The ordered step shape is the single source of
 * truth for the assessed → taxable → exemption → rate → tax-amount walk.
 */

interface CalcStep {
  term: string;
  label: string;
  input: number | string | null;
  output: number | string | null;
  rule: string | null;
}

interface CalcBreakdown {
  tax_record_id: string;
  state: string | null;
  is_millage: boolean;
  steps: CalcStep[];
}

// Terms whose value is a dollar figure (governed money — subject to redaction).
// `circuit_breaker_cap` is the capped appraised value emitted ONLY for a
// circuit-breaker-flagged parcel (item 10.3); formatting it here renders the
// capped step as e.g. "$360,000" rather than the raw "360000".
const MONEY_TERMS = new Set(['current_value', 'circuit_breaker_cap', 'taxable_value', 'exemption_amount', 'tax_amount']);
// Steps whose `input` is a prior dollar figure.
const MONEY_INPUT_TERMS = new Set(['assessment_ratio', 'circuit_breaker_cap', 'taxable_value', 'exemption_amount', 'tax_amount']);

function money(v: number | string | null | undefined): string {
  if (v == null) return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

function formatOutput(step: CalcStep, isMillage: boolean): string {
  const v = step.output;
  if (v == null) return '—';
  if (step.term === 'state') return String(v);
  if (MONEY_TERMS.has(step.term)) return money(v);
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return String(v);
  if (step.term === 'assessment_ratio') return `${n.toFixed(3)} · ${(n * 100).toFixed(1)}%`;
  // A2 — the two multipliers the trace gained. Both are plain factors, not
  // percentages: an Illinois equalization factor is routinely ABOVE 1 (Cook
  // County ~2.9160), so rendering it as a percentage would read as nonsense.
  if (step.term === 'tax_ratio') return n.toFixed(4);
  if (step.term === 'equalization_factor') return `× ${n.toFixed(4)}`;
  if (step.term === 'rate_divisor') return n.toLocaleString();
  if (step.term === 'rate') return `${n.toLocaleString(undefined, { maximumFractionDigits: 4 })} ${isMillage ? 'mills' : '%'}`;
  return String(v);
}

function formatInput(step: CalcStep): string {
  if (step.input == null) return '';
  if (MONEY_INPUT_TERMS.has(step.term)) return money(step.input);
  return String(step.input);
}

function ruleVariant(rule: string | null): 'success' | 'info' | 'neutral' | 'warning' {
  if (!rule) return 'neutral';
  if (rule === 'state' || rule === 'state_rule') return 'success';
  if (rule === 'jurisdiction') return 'info';
  if (rule === 'unseeded_fallback') return 'warning';
  return 'neutral';
}

function ruleLabel(rule: string | null): string {
  if (!rule) return '—';
  return rule.replace(/[_-]/g, ' ');
}

export interface CalcBreakdownPanelProps {
  taxRecordId: string;
}

export function CalcBreakdownPanel({ taxRecordId }: CalcBreakdownPanelProps) {
  const query = useQuery<CalcBreakdown>({
    queryKey: ['calc-breakdown', taxRecordId],
    queryFn: ({ signal }) => api.get<CalcBreakdown>(`/api/tax-records/${taxRecordId}/calc-breakdown`, signal),
    enabled: !!taxRecordId,
  });

  if (query.isLoading) {
    return <Card><p style={muted}>Loading calculation trace…</p></Card>;
  }

  if (query.error) {
    // Staff-only endpoint: a non-staff / off-book / permission-denied requester
    // simply doesn't see the trace — render a quiet notice rather than an alarm.
    const err = query.error;
    const denied = err instanceof ApiClientError && (err.status === 403 || err.status === 401 || err.status === 404);
    return (
      <Card>
        <p style={muted}>
          {denied
            ? 'Calculation trace is not available for your role on this record.'
            : `Could not load the calculation trace: ${(err as Error).message}`}
        </p>
      </Card>
    );
  }

  const data = query.data;
  if (!data || !data.steps || data.steps.length === 0) {
    return <Card><p style={muted}>No calculation trace available for this record.</p></Card>;
  }

  const byTerm = Object.fromEntries(data.steps.map((s) => [s.term, s]));
  const taxAmount = byTerm.tax_amount?.output ?? null;
  const taxableValue = byTerm.taxable_value?.output ?? null;

  return (
    <Card
      right={
        <div style={{ display: 'flex', gap: 'var(--sp-2)', alignItems: 'center' }}>
          {data.state && <Badge variant="neutral">{data.state}</Badge>}
          <Badge variant={data.is_millage ? 'info' : 'neutral'}>
            {data.is_millage ? 'Millage' : 'Percent'}
          </Badge>
        </div>
      }
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 170px), 1fr))', gap: 'var(--sp-3)', marginBottom: 'var(--sp-4)' }}>
        <StatCard label="Taxable value" value={money(taxableValue)} caption="After ratio & exemptions" accent="info" />
        <StatCard label="Tax amount" value={money(taxAmount)} caption={data.is_millage ? 'Millage-based' : 'Percent-based'} accent="hunter" />
      </div>

      <div style={{ overflowX: 'auto' }}>
        <div style={{ minWidth: 480 }}>
          <div style={{ ...gridRow, padding: 'var(--sp-1) 0', borderBottom: '1px solid var(--color-border)' }}>
            <HeadCell>Step</HeadCell>
            <HeadCell align="right">Input</HeadCell>
            <HeadCell align="right">Output</HeadCell>
            <HeadCell>Source</HeadCell>
          </div>
          {data.steps.map((step, idx) => {
            const inputStr = formatInput(step);
            const isFinal = step.term === 'tax_amount';
            return (
              <div
                key={step.term}
                style={{
                  ...gridRow,
                  padding: 'var(--sp-2) 0',
                  borderBottom: idx === data.steps.length - 1 ? 'none' : '1px solid var(--color-border)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-2)' }}>
                  <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', fontVariantNumeric: 'tabular-nums', minWidth: 16 }}>{idx + 1}.</span>
                  <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink)', fontWeight: isFinal ? 'var(--fw-semibold)' : 'var(--fw-medium)' }}>{step.label}</span>
                </div>
                <div style={{ ...numCell, color: 'var(--color-ink-3)' }}>{inputStr || '—'}</div>
                <div style={{ ...numCell, color: 'var(--color-ink)', fontWeight: isFinal ? 'var(--fw-semibold)' : 'var(--fw-medium)' }}>
                  {formatOutput(step, data.is_millage)}
                </div>
                <div>
                  <Badge variant={ruleVariant(step.rule)}>{ruleLabel(step.rule)}</Badge>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

const gridRow: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1.1fr)',
  gap: 'var(--sp-3)',
  alignItems: 'center',
};

const numCell: React.CSSProperties = {
  textAlign: 'right',
  fontSize: 'var(--fs-sm)',
  fontVariantNumeric: 'tabular-nums',
};

const muted: React.CSSProperties = {
  margin: 0,
  color: 'var(--color-muted)',
  fontSize: 'var(--fs-sm)',
};

function HeadCell({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <span style={{
      fontSize: 'var(--fs-xs)',
      fontWeight: 'var(--fw-semibold)',
      textTransform: 'uppercase',
      letterSpacing: 'var(--ls-wide)',
      color: 'var(--color-ink-3)',
      textAlign: align,
    }}>{children}</span>
  );
}

function Card({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', padding: 'var(--sp-4)' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-3)', marginBottom: 'var(--sp-3)' }}>
        <h3 style={{ margin: 0, fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', textTransform: 'uppercase', letterSpacing: 'var(--ls-wider)', color: 'var(--color-ink-3)' }}>
          Tax calculation trace
        </h3>
        {right}
      </div>
      {children}
    </div>
  );
}
